import type { Option, Step } from '@de/types/types';
import type { FlowState } from '@de/providers/dynamicExperienceReducer';
import { readInferredCountryCode } from './readInferredCountryCode';
import { resolveGeolocation } from './resolveGeolocation';

/**
 * The DCS triad (degree / category / subject) lead fields. On GGG
 * (general) pages the host injects `window.HE.contextualDefaults` as
 * `general` for any unspecified dimension. That slug is a "no specific
 * context" sentinel — not a user selection — so it must not seed the
 * bound `<Select>`'s value: the Offerings dimensions service labels the
 * `general` taxonomy node "Undecided", so a seeded value makes the
 * control render "Undecided" instead of its placeholder ("Select a
 * degree" / "Select a category" / "Select a subject"). Filtering it here
 * keeps GGG pages blank (placeholders visible) while the underlying
 * `flowState` still carries `general` for branching / result-URL reads
 * (which already fall back to `general` on a miss). Matches the platform
 * intent that only the submit path substitutes `general`.
 */
const DCS_LEAD_FIELDS: ReadonlySet<string> = new Set([
  'degree',
  'category',
  'subject',
]);
const DCS_GENERAL_SENTINEL = 'general';

function isUnselectedDcsSentinel(leadField: string, value: string): boolean {
  return DCS_LEAD_FIELDS.has(leadField) && value === DCS_GENERAL_SENTINEL;
}

/**
 * Project a DE `flowState` snapshot into the `Record<string, string>`
 * shape that {@link DataSourceManagerProvider} consumes as
 * `initialFormState` (child SAD ADR-10 — DSM `formState` is a flat
 * key/string map keyed by lead field).
 *
 * For each step in `steps`, read the recorded `Option | Option[]`
 * payload and emit one entry per `leadFieldAssociated → value`:
 *   - **Single-Option payload**: emit
 *     `{ [stepLeadField]: option.value }` using the in-flight
 *     `leadFieldAssociated` when present, falling back to the
 *     step-level one (same precedence as
 *     {@link buildFlowContext}).
 *   - **Option[] payload** (multi-question step, or the DCS
 *     auto-population shape written by
 *     {@link initializeAutoPopulation}): emit one entry per option
 *     keyed by that option's own `leadFieldAssociated`. Step-level
 *     aliases already in the seed win on collision — broader
 *     association represents the step's primary write contract and
 *     must not be overwritten by a per-option alias of the same
 *     name.
 *
 * Multi-value step-level selections (Option[] without per-option
 * `leadFieldAssociated`) are intentionally skipped: DSM's
 * `formState` is `Record<string, string>` and does not represent
 * array values today.
 *
 * The primary use case is bridging `window.HE.contextualDefaults`
 * (degree / category / subject) — which
 * {@link initializeAutoPopulation} writes into
 * `flowState[entryStepId].option` as an `Option[]` — into the DSM
 * so DSL `<Select>`s bound to `formState.<leadField>` pre-fill on
 * mount instead of starting empty and forcing the user to re-pick
 * their already-known context.
 *
 * Pure and `O(numSteps × numOptions)`; callers should memoize on
 * `[flowState, steps]`. Never throws.
 */
export function deriveInitialFormState(
  flowState: FlowState | undefined,
  steps: readonly Step[] | undefined,
): Record<string, string> {
  const seed: Record<string, string> = {};
  if (!flowState || !steps) return seed;

  for (const step of steps) {
    const stepId = step?.id;
    if (!stepId) continue;
    const data = flowState[stepId];
    if (!data) continue;

    const opt = data.option as Option | Option[] | undefined;
    if (!opt) continue;

    if (!Array.isArray(opt)) {
      // Option-level association is the host-seed shape: a single owned
      // answer carries `leadFieldAssociated` on the option, not the step.
      const stepLeadField =
        (data.leadFieldAssociated as string | undefined) ??
        (step.leadFieldAssociated as string | undefined) ??
        opt.leadFieldAssociated;
      if (
        typeof stepLeadField === 'string' &&
        stepLeadField.length > 0 &&
        typeof opt.value === 'string' &&
        opt.value.length > 0 &&
        !isUnselectedDcsSentinel(stepLeadField, opt.value)
      ) {
        seed[stepLeadField] = opt.value;
      }
      continue;
    }

    for (const o of opt) {
      const k = o.leadFieldAssociated;
      if (typeof k !== 'string' || k.length === 0) continue;
      if (typeof o.value !== 'string' || o.value.length === 0) continue;
      if (isUnselectedDcsSentinel(k, o.value)) continue;
      if (Object.prototype.hasOwnProperty.call(seed, k)) continue;
      seed[k] = o.value;
    }
  }

  // Surface the IP-inferred country code as a flat formState key. It is
  // written by `initializeAutoPopulation` under the global `location`
  // slot, which is keyed by name (not a step id) and therefore skipped by
  // the step loop above. DSL data sources bind it via
  // `{{formState.countryCode}}` (e.g. the offerings budget `geolocation`).
  // A step-derived `countryCode` already in the seed wins on collision.
  const inferredCountryCode = readInferredCountryCode(flowState);
  if (
    inferredCountryCode &&
    !Object.prototype.hasOwnProperty.call(seed, 'countryCode')
  ) {
    seed['countryCode'] = inferredCountryCode;
  }

  // Surface the resolved geolocation (`CC` or `CC-SUB`) as a flat formState
  // key. The `location` slot merges the user's answered location question
  // (ZIP → resolved `stateAbbr`) over the IP-inferred region, so
  // `resolveGeolocation` already encodes the "question wins, else IP"
  // precedence. DSL offerings data sources bind it via
  // `{{formState.geolocation}}`. Country-only is a valid bind value; the
  // field is left unset only when there is no usable country yet.
  //
  // This intentionally makes the live `location` slot the sole authority for
  // `geolocation`, OVERRIDING any value already in the seed from the step
  // loop above. The only producer of a step-level `geolocation` lead field is
  // the entry DCS step's submission snapshot — itself a copy of
  // `formState.geolocation` frozen at entry-step submit time (i.e. the
  // IP-inferred region at page load). On a later step (e.g. the results page)
  // that snapshot is stale: the user may have answered a ZIP question that
  // moved `location.state`. Deriving from the live `location` slot here keeps
  // the region authoritative and avoids racing the runtime bridge in
  // `ConfigurableStep`, which would otherwise have to correct a stale seed
  // after the first offerings fetch. When the live slot yields no usable
  // country the stale snapshot is dropped (not preserved) so the bound token
  // resolves to nothing rather than shipping a frozen page-load value.
  const geolocation = resolveGeolocation(flowState);
  if (geolocation) {
    seed['geolocation'] = geolocation;
  } else {
    delete seed['geolocation'];
  }

  return seed;
}
